export type FeatureCategory = 'fan' | 'operator';

export interface Feature {
  icon: string;
  title: string;
  description: string;
  details: string;
  points: string[];
  category: FeatureCategory;
}

export const features: Feature[] = [
  {
    icon: '📍',
    title: 'Seat-aware routing',
    description:
      'Automatically routes every order to the correct section, row and seat — so runners spend less time searching and more time delivering.',
    details:
      'Every order in ONI carries precise seat metadata from checkout through delivery. Runners receive clear routing instructions, vendors see fulfillment destinations in the live queue, and venue teams can audit delivery accuracy by section.',
    points: [
      'Section, row, and seat attached at checkout',
      'Runners get turn-by-turn routing within the venue',
      'Reduces missed deliveries and fan frustration',
    ],
    category: 'operator',
  },
  {
    icon: '📱',
    title: 'Live order tracking',
    description:
      'Give fans real-time visibility from kitchen to seat while your team updates status from a single live queue.',
    details:
      'Fans follow their order from confirmed to preparing, ready, out for delivery, and delivered — without refreshing or calling concessions. Operators update status from one dashboard, keeping both sides aligned during peak game-day volume.',
    points: [
      'Adaptive status updates during live events',
      'Single source of truth for fans and staff',
      'Fewer "where is my order?" inquiries',
    ],
    category: 'fan',
  },
  {
    icon: '🔁',
    title: 'Higher repeat orders',
    description:
      'One-tap reorder brings fans back for seconds without rebuilding carts — driving more revenue per guest.',
    details:
      'Order history lets fans reorder favorites in seconds. Venues benefit from higher per-cap concession spend without adding staff — fans who already trust the experience come back for more during the same event.',
    points: [
      'One-tap reorder from order history',
      'Faster second-round ordering on game day',
      'Increases average order value per guest',
    ],
    category: 'fan',
  },
  {
    icon: '🍔',
    title: 'Multi-vendor marketplace',
    description:
      'Run multiple concessions under one venue platform with menus, modifiers, promos, and dietary info in one flow.',
    details:
      'ONI supports a multi-vendor marketplace under one venue umbrella. Fans browse concessions across the stadium in a single ordering flow while each vendor manages their own menu, modifiers, availability, and promotions from the admin dashboard.',
    points: [
      'Multiple concessions live per event',
      'Per-vendor menu and modifier control',
      'Venue-wide promos and dietary labeling',
    ],
    category: 'operator',
  },
  {
    icon: '🔐',
    title: 'Frictionless fan access',
    description:
      'No app downloads. No passwords. Fans simply scan, verify, and order — keeping adoption high on game day.',
    details:
      'Fans scan a QR code, verify with a one-time code, and start ordering immediately. No app store download, no account password to remember — removing the biggest friction points that kill adoption at live events.',
    points: [
      'QR scan to start ordering',
      'Secure one-time verification — no passwords',
      'Works in mobile browser, no app required',
    ],
    category: 'fan',
  },
  {
    icon: '⏱️',
    title: 'Smart ETAs',
    description:
      'Estimated delivery times factor in prep time and walk distance from vendor to section, setting accurate fan expectations.',
    details:
      'ONI calculates estimated delivery times using vendor prep time and the walk distance from concession to the fan\'s section. Accurate ETAs reduce complaints, help runners prioritize, and set realistic expectations during high-volume periods.',
    points: [
      'Prep time plus walk distance factored in',
      'ETAs update as queue volume changes',
      'Helps fans plan without leaving their seats',
    ],
    category: 'operator',
  },
];

export const featureCategories: { id: FeatureCategory; label: string; subtitle: string }[] = [
  {
    id: 'fan',
    label: 'Fan experience',
    subtitle: 'What your guests get — fast ordering without missing the action.',
  },
  {
    id: 'operator',
    label: 'Operator tools',
    subtitle: 'What your venue team controls — routing, vendors, and live fulfillment.',
  },
];
